import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["disabled", "value", "label", "inputProps", "onPickChange", "format", "locale", "disableFuture", "popperProps", "id"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* © 2023 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { EmbersInnerThemeProvider } from '../rtlProvider';
import { LocalizationProvider } from './lab/src';
import { CustomDateAdapter } from './base';
import { EmbersDateCalendar } from './dateCalendar';
import { EmbersDateField } from './dateField';
import { datePickerTheme, StyledDatePickerPopper, StyledDateInput } from './dateStyles';
import { getFormat, handleTabKey, convertToMoment, focusToFirstActiveElement } from '../utils/dateUtils';
import { onKeyPress } from '../utils/wcagUtils';
import { useId } from '../utils/useId';
function EmbersDatePicker(props) {
  var disabled = props.disabled,
    value = props.value,
    label = props.label,
    _props$inputProps = props.inputProps,
    inputProps = _props$inputProps === void 0 ? {} : _props$inputProps,
    onPickChange = props.onPickChange,
    format = props.format,
    locale = props.locale,
    _props$disableFuture = props.disableFuture,
    disableFuture = _props$disableFuture === void 0 ? true : _props$disableFuture,
    popperProps = props.popperProps,
    _id = props.id,
    otherProps = _objectWithoutProperties(props, _excluded);
  var inputRef = useRef(null);
  var id = useId(_id);
  var intl = useIntl();
  var dateFormat = getFormat(format, locale);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    date = _useState2[0],
    setDate = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    open = _useState4[0],
    setOpen = _useState4[1];
  useEffect(function () {
    if (value) {
      setDate(convertToMoment(value));
    }
  }, [value]);
  var focusToInput = function focusToInput() {
    focusToFirstActiveElement(".EmbersDatePicker-input-".concat(id));
  };
  var handleInputChange = function handleInputChange(newDate) {
    setDate(moment.isDate(newDate) ? moment(newDate) : newDate);
    onPickChange === null || onPickChange === void 0 || onPickChange((moment.isDate(newDate) ? newDate : newDate === null || newDate === void 0 ? void 0 : newDate.toDate()) || null);
  };
  var handlePickerChange = function handlePickerChange(newDate, eventType) {
    setDate(moment.isDate(newDate) ? moment(newDate) : newDate);
    onPickChange === null || onPickChange === void 0 || onPickChange(newDate || null);
    setOpen(false);
    if (eventType !== 'click') {
      focusToInput();
    }
  };
  return /*#__PURE__*/React.createElement(EmbersInnerThemeProvider, {
    theme: datePickerTheme
  }, /*#__PURE__*/React.createElement(LocalizationProvider, {
    dateAdapter: CustomDateAdapter,
    locale: locale
  }, /*#__PURE__*/React.createElement(StyledDateInput, null, /*#__PURE__*/React.createElement(EmbersDateField, _extends({
    inputRef: inputRef,
    className: "EmbersDatePicker-input-".concat(id),
    value: date,
    disabled: disabled,
    locale: locale,
    format: dateFormat,
    disableFuture: disableFuture,
    disablePast: otherProps.disablePast,
    handlePickerChange: handleInputChange
  }, inputProps, {
    inputProps: _objectSpread({
      id: "EmbersDatePicker-input-".concat(id)
    }, inputProps.inputProps || {}),
    InputProps: _objectSpread({
      label: label,
      endAdornment: /*#__PURE__*/React.createElement("button", {
        className: "EmbersDatePicker-inputIcon icon-calendar",
        tabIndex: 0,
        disabled: disabled,
        onClick: function onClick() {
          return setOpen(true);
        },
        "aria-label": intl.formatMessage({
          id: 'embers.date.eA.calendarIcon',
          defaultMessage: 'Choose date'
        }),
        onKeyDown: function onKeyDown(e) {
          return onKeyPress(e, ['Enter', ' '], function () {
            setOpen(true);
            e.preventDefault();
          });
        }
      })
    }, inputProps.InputProps || {})
  }))), /*#__PURE__*/React.createElement(StyledDatePickerPopper, _extends({
    id: "".concat(id, "-popper"),
    className: "EmbersDatePicker-popper",
    anchorEl: inputRef.current,
    disableCloseOnTab: true,
    onEscape: function onEscape() {
      setOpen(false);
    },
    open: open,
    onKeyDown: handleTabKey(["#".concat(id, "-popper .MuiCalendarPicker-root"), ".EmbersDatePicker-input-".concat(id)])
  }, popperProps), /*#__PURE__*/React.createElement(EmbersDateCalendar, _extends({
    format: dateFormat,
    value: date,
    onPickChange: handlePickerChange,
    disableFuture: disableFuture
  }, otherProps)))));
}
export { EmbersDatePicker };